/**
 * CSRF token handling for the cookie-authenticated web session.
 *
 * The API issues two cookies: an HttpOnly session cookie the browser sends by
 * itself, and a JS-readable `<session>_csrf` cookie holding the token that must
 * be echoed in `X-CSRF-Token` on every unsafe request. Bearer-token callers (the
 * iPad, the MCP server) are exempt and send neither.
 *
 * The token is held in memory as the primary source — it comes back in the body
 * of every login, register and session call — with the cookie as the fallback
 * that lets a freshly opened tab issue a write before it has read the session.
 */

/** Header the API expects. `X-XSRF-TOKEN` is also accepted server-side. */
export const CSRF_HEADER = 'X-CSRF-Token'

/**
 * Name of the HttpOnly session cookie. It is `SESSION_COOKIE_NAME` on the
 * server (default "web"); the readable CSRF cookie is that name plus `_csrf`.
 * Override with VITE_SESSION_COOKIE when the deployment renames it.
 */
const SESSION_COOKIE: string =
  (import.meta.env.VITE_SESSION_COOKIE as string | undefined) ?? 'web'

let inMemoryToken: string | null = null

/** Records the token returned by a session, login or register response. */
export function setCsrfToken(token: string | null | undefined): void {
  inMemoryToken = token ? token : null
}

/** Forgets the token. Called on sign-out so a stale value is never replayed. */
export function clearCsrfToken(): void {
  inMemoryToken = null
}

/**
 * Reads the readable CSRF cookie.
 *
 * Falls back to any cookie whose name ends in `_csrf`, so a deployment that
 * sets SESSION_COOKIE_NAME without also setting VITE_SESSION_COOKIE still
 * works rather than silently failing every write with a 419.
 */
export function readCsrfCookie(): string | null {
  if (typeof document === 'undefined') return null

  const wanted = `${SESSION_COOKIE}_csrf`
  let fallback: string | null = null

  for (const part of document.cookie.split(';')) {
    const index = part.indexOf('=')
    if (index < 1) continue
    const name = part.slice(0, index).trim()
    const value = decodeURIComponent(part.slice(index + 1).trim())
    if (!value) continue
    if (name === wanted) return value
    if (name.endsWith('_csrf')) fallback = value
  }
  return fallback
}

/** The token to send, preferring the value the API most recently handed us. */
export function currentCsrfToken(): string | null {
  return inMemoryToken ?? readCsrfCookie()
}
